import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { 
  ArrowRight, 
  Search, 
  Code2, 
  TrendingUp, 
  Sparkles, 
  BarChart3, 
  ArrowUpRight,
  Zap,
  CheckCircle2
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const seoSlides = [
  {
    category: "Search Engineering",
    title: "Technical SEO & Search Dominance",
    desc: "We don't just optimize for search engine crawlers; we align with user search intent. By rebuilding taxonomy, expanding semantic authority, and maximizing speed, we construct search funnels that capture high-intent buyers organically.",
    icon: Search,
    color: "var(--glow-cyan)",
    bullets: [
      "Semantic topic clustering & intent keyword mapping",
      "Rich snippet schemas & JSON-LD structured data integration",
      "Crawl budget optimization & server-side rendering (SSR)",
      "Technical Core Web Vitals audit and low-latency rendering"
    ],
    metric: "1.8M+ Organic Reach",
    subtitle: "Semantic Organic Pipelines"
  },
  {
    category: "Creative Technology",
    title: "High-Performance Conversion Web Apps",
    desc: "A beautiful website is a cost unless it converts. We build lightning-fast web applications optimized for speed, security, and search engine visibility using React, Next.js, and modern Jamstack architectures.",
    icon: Code2,
    color: "var(--glow-orange)",
    bullets: [
      "JAMstack & static site generation for instant loading",
      "99+ Google PageSpeed and Lighthouse optimization",
      "Fully responsive, accessible (WCAG) component engineering",
      "Integrated web analytics and multi-touch conversion tracking"
    ],
    metric: "99+ Performance Score",
    subtitle: "Conversion-Focused Code"
  },
  {
    category: "Performance Marketing",
    title: "ROAS-Driven Paid Acquisition",
    desc: "Paid media scaling is driven by testing velocity. We build and run data-backed programmatic and social campaigns across Meta, Google, and premium networks to capture attention and scale customer acquisition profitably.",
    icon: TrendingUp,
    color: "var(--glow-purple)",
    bullets: [
      "High-velocity A/B testing models & ad-creative matrix",
      "First-party Conversions API & advanced pixel configurations",
      "Precision audience clustering & dynamic lookalike modeling",
      "ROAS-led media buying, budget pacing and attribution tracking"
    ],
    metric: "4.2x Average ROAS",
    subtitle: "Paid Scaling Engines"
  },
  {
    category: "Brand Narrative",
    title: "Attention-Grabbing Creative Content",
    desc: "Narrative is the ultimate brand multiplier. We write compelling copy and produce high-fidelity creative content—cinematic reels, vertical video scripts, and digital identity systems—designed to stand out in saturated feeds.",
    icon: Sparkles,
    color: "var(--glow-cyan)",
    bullets: [
      "Social-first cinematic content production (Reels, TikToks)",
      "Cohesive brand design systems and modern web typography",
      "High-conversion, persuasive copywriting and authority assets",
      "Visual identity guidelines and brand storytelling positioning"
    ],
    metric: "8M+ Reach / month",
    subtitle: "Attention Engineering"
  },
  {
    category: "Growth & Analytics",
    title: "Compounding Growth Analytics",
    desc: "Scaling is a science of feedback loops. We build centralized data systems to trace the entire customer lifecycle, identifying conversion bottlenecks and implementing retention strategies that maximize customer lifetime value (LTV).",
    icon: BarChart3,
    color: "var(--glow-orange)",
    bullets: [
      "Multi-channel attribution modeling and unified dashboards",
      "Customer lifetime value (LTV) and repurchase optimization",
      "Funnel drop-off analysis and heatmap behavior insights",
      "Growth consultation, brand positioning, and cohort analysis"
    ],
    metric: "+312% Growth Scale",
    subtitle: "Strategy & Intelligence"
  }
];

export function HorizontalSEO() {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !scrollSectionRef.current) return;

    const scrollSection = scrollSectionRef.current;
    
    const getScrollAmount = () => {
      return -(scrollSection.scrollWidth - window.innerWidth);
    };

    const pinTrigger = ScrollTrigger.create({
      trigger: containerRef.current,
      start: "top top",
      end: () => `+=${scrollSection.scrollWidth - window.innerWidth}`,
      pin: true,
      scrub: 0.2, // Fast, responsive catch-up to prevent unpinning lag
      anticipatePin: 1, // Smooth out entrance transitions
      invalidateOnRefresh: true,
      animation: gsap.to(scrollSection, {
        x: getScrollAmount,
        ease: "none",
        force3D: true, // Hardware acceleration
      }),
    });

    // Refresh layout calculations after mounting
    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 150);

    return () => {
      pinTrigger.kill();
      clearTimeout(timer);
    };
  }, []);

  return (
    <section
      ref={containerRef}
      id="growth-blueprint"
      className="relative w-full overflow-hidden bg-black text-white"
    >
      {/* Background Orbs */}
      <div className="absolute top-1/4 left-1/4 h-[350px] w-[350px] rounded-full bg-[var(--glow-purple)] blur-[120px] opacity-15 pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-[var(--glow-cyan)] blur-[130px] opacity-10 pointer-events-none" />

      {/* Horizontal Scroll Track Wrapper */}
      <div 
        ref={scrollSectionRef} 
        className="flex h-screen items-center pt-24 md:pt-28 pb-8 md:pb-12 pl-6 pr-12 md:pl-20 md:pr-24 will-change-transform"
      >
        {/* Intro Section Card */}
        <div className="flex h-[calc(100vh-8rem)] min-h-[460px] max-h-[600px] lg:max-h-[640px] w-[90vw] max-w-[460px] flex-col justify-between shrink-0 p-6 sm:p-8 md:p-10 mr-6 md:mr-14 rounded-3xl bg-[#08080d] border border-white/10 shadow-2xl relative overflow-hidden select-none will-change-transform">
          <div className="absolute inset-0 bg-gradient-to-tr from-[rgba(168,85,247,0.06)] to-transparent pointer-events-none" />
          
          <div>
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.25em] text-muted-foreground bg-white/5 border border-white/5">
              <Zap className="h-3 w-3 text-[var(--glow-orange)]" />
              Scaling Engine
            </div>
            <h2 className="mt-6 md:mt-8 font-display text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] tracking-tight whitespace-normal text-white">
              The <span className="text-gradient">Growth</span> <br />
              Blueprint.
            </h2>
            <p className="mt-4 md:mt-6 text-xs sm:text-sm text-muted-foreground whitespace-normal leading-relaxed max-w-sm">
              We integrate authority-based organic search marketing, low-latency technical codebases, and high-velocity creative campaigns to scale brands.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground pt-4 border-t border-white/5">
            <span>Scroll to explore</span>
            <ArrowRight className="h-4 w-4 text-[var(--glow-orange)] animate-pulse" />
          </div>
        </div>

        {/* Dynamic SEO/Growth Strategy Cards */}
        {seoSlides.map((s, idx) => {
          const Icon = s.icon;
          return (
            <div
              key={idx}
              className="flex h-[calc(100vh-8rem)] min-h-[460px] max-h-[600px] lg:max-h-[640px] w-[88vw] sm:w-[420px] md:w-[460px] lg:w-[480px] flex-col justify-between shrink-0 p-6 sm:p-7 md:p-9 mr-6 md:mr-14 rounded-3xl bg-[#08080d] border border-white/10 shadow-2xl relative overflow-hidden select-none will-change-transform"
              style={{
                borderTop: `2px solid ${s.color}`,
              }}
            >
              {/* Background gradient hint */}
              <div 
                className="absolute -top-24 -right-24 h-48 w-48 rounded-full blur-3xl opacity-10"
                style={{ backgroundColor: s.color }}
              />

              {/* Card Header */}
              <div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
                    <span 
                      className="h-2 w-2 rounded-full" 
                      style={{ 
                        backgroundColor: s.color, 
                        boxShadow: `0 0 10px ${s.color}` 
                      }}
                    />
                    {s.category}
                  </span>
                </div>

                <h3 className="mt-4 sm:mt-5 font-display text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight whitespace-normal text-white">
                  {s.title}
                </h3>
                <h4 className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mt-1 whitespace-normal">
                  {s.subtitle}
                </h4>
                
                <p className="mt-2.5 sm:mt-3 text-xs md:text-sm text-muted-foreground whitespace-normal leading-relaxed">
                  {s.desc}
                </p>
              </div>

              {/* Bullets List */}
              <ul className="space-y-1.5 sm:space-y-2 my-2 sm:my-3 whitespace-normal">
                {s.bullets.map((bullet, bIdx) => (
                  <li key={bIdx} className="flex items-start gap-2.5 text-[11px] sm:text-xs text-muted-foreground/90">
                    <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 mt-0.5" style={{ color: s.color }} />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-white/5 pt-3.5 md:pt-4 shrink-0">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">
                  METHODOLOGY 0{idx + 1}
                </span>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider hover:text-white transition"
                  style={{ color: s.color }}
                >
                  Apply Framework <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          );
        })}

        {/* Spacer at the end to ensure the last card is fully visible and centered before unpinning */}
        <div className="w-[30vw] md:w-[45vw] shrink-0" />
      </div>
    </section>
  );
}
